import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  CircularProgress,
  Radio,
  Chip,
  Stack,
  Divider,
  Alert,
  Button
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useAppContext } from '../context/AppContext';
import { useAuth } from '../auth/AuthContext';
import { energyService } from '../api/energyApi';
import { useEnergyContext } from '../context/EnergyContext';
import { createContextAwareApiClient } from '../api/contextAwareApiClient';
import InlineEditForm from './InlineEditForm';

function extractCachedCanopies(rawInventory, fleetId) {
  const list = Array.isArray(rawInventory?.data)
    ? rawInventory.data
    : (Array.isArray(rawInventory) ? rawInventory : []);
  if (!fleetId) return list;
  return list.filter((c) => !c?.fleetId || c.fleetId === fleetId);
}

function buildCanopyNamesMap(canopyList) {
  return (Array.isArray(canopyList) ? canopyList : []).reduce((acc, canopy) => {
    const id = canopy?.canopyId || canopy?.canopy_id;
    const name = canopy?.canopyName || canopy?.canopy_name;
    if (id && name && String(name).trim()) {
      acc[id] = String(name).trim();
    }
    return acc;
  }, {});
}

/**
 * FleetCanopyList component provides a scrollable list of canopies in the selected fleet
 * with radio button selection for up to 3 canopies. This component is used in the fleet view
 * to allow users to select multiple canopies for comparison.
 */
function FleetCanopyList({ onCanopySelection }) {
  const auth = useAuth();
  const scopeRef = useRef({});
  const { 
    customerContext,
    fleetContext,
    getContextData,
    validateContext 
  } = useAppContext();
  const energyContext = useEnergyContext();
  
  scopeRef.current = auth.isSuperuser ? { customerId: customerContext?.customerId } : {};

  // Use ref to store the service to prevent infinite loops
  const canopyServiceRef = useRef();
  canopyServiceRef.current = createContextAwareApiClient(getContextData, validateContext).canopyService;
  const cachedCanopiesRef = useRef([]);
  cachedCanopiesRef.current = extractCachedCanopies(
    energyContext?.raw?.canopyInventory,
    fleetContext?.fleetId
  );
  
  const [canopies, setCanopies] = useState(() => cachedCanopiesRef.current);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedCanopies, setSelectedCanopies] = useState([]);
  const [retryCount, setRetryCount] = useState(0);
  
  // Canopy creation state
  const [isCreatingCanopy, setIsCreatingCanopy] = useState(false);
  const [newCanopyData, setNewCanopyData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Canopy form fields configuration
  const canopyFields = [
    {
      name: 'canopyId',
      label: 'Canopy ID',
      type: 'text',
      required: true,
      validation: (value) => {
        if (!value || value.trim() === '') return 'Canopy ID is required';
        if (value.length > 255) return 'Canopy ID must be 255 characters or less';
        return null;
      }
    },
    {
      name: 'canopyName',
      label: 'Canopy Name',
      type: 'text',
      required: false,
      validation: (value) => {
        if (value && value.length > 255) return 'Canopy name must be 255 characters or less';
        return null;
      }
    },
    {
      name: 'canopyCommissioningDate',
      label: 'Commissioning Date',
      type: 'date',
      required: false,
      validation: (value) => {
        if (value) {
          const date = new Date(value);
          if (isNaN(date.getTime())) return 'Invalid date format';
        }
        return null;
      }
    },
    {
      name: 'canopyData',
      label: 'Additional Data',
      type: 'json',
      required: false,
      validation: (value) => {
        if (value) {
          try {
            JSON.parse(value);
          } catch (e) {
            return 'Invalid JSON format';
          }
        }
        return null;
      }
    }
  ];

  // Memoized fetch function to prevent unnecessary re-renders
  const fetchCanopies = useCallback(async (fleetId, retryAttempt = 0) => {
    if (!fleetId) {
      console.log('🚫 FLEET CANOPY LIST: No fleet ID provided');
      return;
    }

    console.log(`🔄 FLEET CANOPY LIST: Starting canopy fetch (attempt ${retryAttempt + 1})`);
    console.log('📋 Fleet ID:', fleetId);
    
    if (cachedCanopiesRef.current.length === 0) {
      setLoading(true);
    }
    setError(null);
    
    try {
      console.log('🌐 FLEET CANOPY LIST: Calling API for fleet:', fleetId);
      const response = await energyService.getFleetCanopies(fleetId, scopeRef.current);
      console.log('📡 FLEET CANOPY LIST: API response received:', response);
      
      if (response.success && response.data) {
        const canopyData = response.data.data || (Array.isArray(response.data) ? response.data : []);
        console.log('📊 FLEET CANOPY LIST: Canopy data extracted:', canopyData);
        console.log('🔢 FLEET CANOPY LIST: Number of canopies:', canopyData.length);
        
        if (canopyData.length > 0) {
          console.log('✅ FLEET CANOPY LIST: Canopies found, updating state');
          canopyData.forEach((canopy, index) => {
            console.log(`   Canopy ${index + 1}: ${canopy.canopyId} - ${canopy.canopyName || 'Unnamed'}`);
          });
        } else {
          console.log('⚠️ FLEET CANOPY LIST: No canopies found for fleet');
        }
        
        setCanopies(canopyData);
        setRetryCount(0); // Reset retry count on success
      } else {
        const isTransientError = !response.status || response.status >= 500 || /timeout|network/i.test(response.message || '');
        if (isTransientError && retryAttempt < 2) {
          console.log(`🔄 FLEET CANOPY LIST: Transient API error, retrying... (${retryAttempt + 1}/3)`);
          setRetryCount(retryAttempt + 1);
          setTimeout(() => {
            fetchCanopies(fleetId, retryAttempt + 1);
          }, 1000);
          return;
        }
        if (cachedCanopiesRef.current.length > 0) {
          setCanopies(cachedCanopiesRef.current);
          setError(null);
        } else {
          console.error('❌ FLEET CANOPY LIST: Failed to load canopies:', response);
          setError(`Failed to load canopies: ${response.message || 'Unknown error'}`);
          setCanopies([]);
        }
      }
    } catch (err) {
      console.error('💥 FLEET CANOPY LIST: Error fetching canopies:', err);
      
      // Retry logic for network errors
      if ((err.message === 'Network Error' || /timeout|network/i.test(err.message || '')) && retryAttempt < 2) {
        console.log(`🔄 FLEET CANOPY LIST: Network error, retrying... (${retryAttempt + 1}/3)`);
        setRetryCount(retryAttempt + 1);
        
        // Wait 1 second before retrying
        setTimeout(() => {
          fetchCanopies(fleetId, retryAttempt + 1);
        }, 1000);
        return;
      }
      
      if (cachedCanopiesRef.current.length > 0) {
        setCanopies(cachedCanopiesRef.current);
        setError(null);
      } else {
        setError(`Error: ${err.message}`);
        setCanopies([]);
      }
    } finally {
      setLoading(false);
      console.log('🏁 FLEET CANOPY LIST: Canopy fetch completed');
    }
  }, []);

  useEffect(() => {
    const cached = extractCachedCanopies(energyContext?.raw?.canopyInventory, fleetContext?.fleetId);
    if (cached.length > 0) {
      setCanopies((prev) => (prev.length === 0 ? cached : prev));
      setError(null);
    }
  }, [energyContext?.raw?.canopyInventory, fleetContext?.fleetId]);

  // Fetch canopies when the fleet context changes
  useEffect(() => {
    if (!fleetContext) {
      console.log('🚫 FLEET CANOPY LIST: No fleet context, clearing canopies');
      setCanopies([]);
      setSelectedCanopies([]);
      setError(null);
      setRetryCount(0);
      return;
    }
    
    // Only fetch if we have a valid fleet ID
    if (fleetContext.fleetId) {
      if (cachedCanopiesRef.current.length > 0) {
        setCanopies(cachedCanopiesRef.current);
      }
      fetchCanopies(fleetContext.fleetId);
    }
  }, [fleetContext, fetchCanopies]);

  // Handle canopy selection with radio button
  const handleCanopySelect = (canopyId) => {
    const namesMap = buildCanopyNamesMap(canopies);
    setSelectedCanopies(prev => {
      // If already selected, remove it
      if (prev.includes(canopyId)) {
        const newSelection = prev.filter(id => id !== canopyId);
        // Notify parent of selection change
        if (onCanopySelection) {
          onCanopySelection(newSelection, namesMap);
        }
        return newSelection;
      }
      
      // If not selected and under limit, add it
      if (prev.length < 3) {
        const newSelection = [...prev, canopyId];
        // Notify parent of selection change
        if (onCanopySelection) {
          onCanopySelection(newSelection, namesMap);
        }
        return newSelection;
      }
      
      // If at limit, replace the last one
      const newSelection = [...prev.slice(0, 2), canopyId];
      // Notify parent of selection change
      if (onCanopySelection) {
        onCanopySelection(newSelection, namesMap);
      }
      return newSelection;
    });
  };

  // Clear all selections
  const clearSelections = () => {
    const namesMap = buildCanopyNamesMap(canopies);
    setSelectedCanopies([]);
    // Notify parent of selection change
    if (onCanopySelection) {
      onCanopySelection([], namesMap);
    }
  };

  // Get canopy display name
  const getCanopyDisplayName = (canopy) => {
    if (canopy.canopyName && canopy.canopyName.trim()) {
      return canopy.canopyName;
    }
    return canopy.canopyId;
  };

  // Get canopy status from canopy data
  const getCanopyStatus = (canopy) => {
    try {
      if (canopy.canopyData) {
        const data = typeof canopy.canopyData === 'string' 
          ? JSON.parse(canopy.canopyData) 
          : canopy.canopyData;
        return data.status || 'active';
      }
    } catch (e) {
      console.warn('Failed to parse canopy data:', e);
    }
    return 'active';
  };

  // Handle retry
  const handleRetry = () => {
    if (fleetContext?.fleetId) {
      fetchCanopies(fleetContext.fleetId);
    }
  };

  // Handle new canopy creation
  const handleAddCanopy = () => {
    setNewCanopyData({
      canopyId: '',
      fleetId: fleetContext?.fleetId,
      canopyName: '',
      canopyCommissioningDate: '',
      canopyData: {}
    });
    setIsCreatingCanopy(true);
  };

  const handleCancelCreate = () => {
    setIsCreatingCanopy(false);
    setNewCanopyData(null);
  };

  const handleSaveCanopy = async (formData) => {
    setIsSubmitting(true);
    try {
      const canopyPayload = { ...formData, fleetId: fleetContext?.fleetId };
      const response = await canopyServiceRef.current.createCanopy(canopyPayload);
      
      if (response.success) {
        // Refresh the canopy list
        await fetchCanopies(fleetContext.fleetId);
        setIsCreatingCanopy(false);
        setNewCanopyData(null);
      } else {
        console.error('Failed to create canopy:', response.message);
        // You might want to show an error notification here
      }
    } catch (err) {
      console.error('Error creating canopy:', err);
      // You might want to show an error notification here
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!fleetContext) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="textSecondary">
          Please select a fleet to view canopies
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <CircularProgress size={24} />
        <Typography variant="body2" sx={{ mt: 1 }}>
          Loading Canopies...{retryCount > 0 ? ` (Retry ${retryCount})` : ''}
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Typography 
          variant="body2" 
          color="primary" 
          sx={{ cursor: 'pointer', textDecoration: 'underline' }}
          onClick={handleRetry}
        >
          Click to retry
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h6" component="div">
            Fleet Canopies
          </Typography>
          <Chip 
            label={`${canopies.length} canopies`} 
            size="small" 
            color="primary" 
            variant="outlined"
          />
          {!auth.isGuest && (
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAddCanopy}
            disabled={!fleetContext}
          >
            New Canopy
          </Button>
          )}
        </Stack>
        
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2" color="textSecondary">
            Select up to 3 canopies for comparison
          </Typography>
          {selectedCanopies.length > 0 && (
            <Chip 
              label={`${selectedCanopies.length}/3 selected`} 
              size="small" 
              color="secondary"
            />
          )}
        </Stack>
        
        {selectedCanopies.length > 0 && (
          <Typography 
            variant="body2" 
            color="primary" 
            sx={{ cursor: 'pointer', textDecoration: 'underline', mt: 1 }}
            onClick={clearSelections}
          >
            Clear selections
          </Typography>
        )}
      </Box>

      {/* Canopy List */}
      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        {canopies.length === 0 ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="textSecondary">
              No canopies found in this fleet
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {canopies.map((canopy, index) => {
              const isSelected = selectedCanopies.includes(canopy.canopyId);
              const status = getCanopyStatus(canopy);
              
              return (
                <React.Fragment key={canopy.canopyId}>
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={() => handleCanopySelect(canopy.canopyId)}
                      selected={isSelected}
                      sx={{
                        '&.Mui-selected': {
                          backgroundColor: 'primary.light',
                          '&:hover': {
                            backgroundColor: 'primary.light',
                          },
                        },
                      }}
                    >
                      <Radio
                        checked={isSelected}
                        disabled={!isSelected && selectedCanopies.length >= 3}
                        size="small"
                      />
                      <ListItemText
                        secondaryTypographyProps={{ component: 'div' }}
                        primary={getCanopyDisplayName(canopy)}
                        secondary={
                          <Box>
                            <Typography variant="caption" component="div">
                              {canopy.canopyId}
                            </Typography>
                            <Chip 
                              label={status} 
                              size="small" 
                              color={status === 'active' ? 'success' : 'warning'}
                              variant="outlined"
                              sx={{ mt: 0.5 }}
                            />
                          </Box>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                  {index < canopies.length - 1 && <Divider />}
                </React.Fragment>
              );
            })}
          </List>
        )}
      </Box>
      
      {/* Canopy Creation Dialog */}
      {!auth.isGuest && isCreatingCanopy && (
        <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
          <InlineEditForm
            data={newCanopyData}
            fields={canopyFields}
            onSave={handleSaveCanopy}
            onCancel={handleCancelCreate}
            isSubmitting={isSubmitting}
            title="Create New Canopy"
          />
        </Box>
      )}
    </Box>
  );
}

export default FleetCanopyList; 